/**
 * 1.7.5 — charts, one way, everywhere (D-1.7.5-1). A chart is its numbers first: a title, the categories (the rows
 * of its table) and one or more series (the columns). The screen (`src/components/Chart.tsx`) and the letterhead
 * PDF (`api/src/pdf/chart.ts`) both draw from this one shape, and both put the same table under the drawing — so
 * the picture can never say something its numbers do not.
 *
 * Four shapes: bar, horizontal bar, line (a trend over time), donut (a share). 2D only — a 3D bar or pie misreads
 * (perspective makes equal values look unequal) and says nothing to a screen reader. Pure: nothing here draws.
 */

export const CHART_KINDS = ['bar', 'hbar', 'line', 'donut'] as const;
export type ChartKind = (typeof CHART_KINDS)[number];

export interface ChartSeries {
  /** The column's header in the table, and the legend's words. */
  name: string;
  /** One value per category, in the categories' order. */
  values: number[];
}

export interface ChartSpec {
  kind: ChartKind;
  /** Always shown: the heading over the drawing, the table's caption. */
  title: string;
  /** One line under the title: what is counted, and when. */
  note?: string;
  /** The header of the table's first column ("Event", "Month", "Answer"). */
  categoryLabel: string;
  /** The rows: a bar each, a point in time each (line), a slice each (donut). */
  categories: string[];
  /** One series is one colour, one pattern and one column. A donut draws the first series only. */
  series: ChartSeries[];
  /** What a value is, after the number in the table and on a label ("people", "min"). */
  unit?: string;
}

/* ------------------------------------------------------------------------------------------------
 * The table — the same numbers the drawing shows
 * ---------------------------------------------------------------------------------------------- */

const whole = (n: number) => Math.abs(n - Math.round(n)) < 1e-9;
/** A value as it is written: whole numbers as they are, others to one decimal. */
export const chartNumber = (n: number): string => (Number.isFinite(n) ? (whole(n) ? String(Math.round(n)) : n.toFixed(1)) : '0');

/** A slice's share of the whole, in whole per cent (0 when the whole is nothing). */
export const sharePercent = (value: number, total: number): number => (total > 0 ? Math.round((value / total) * 100) : 0);

export interface ChartTable {
  caption: string;
  head: string[];
  rows: string[][];
}

/**
 * The chart as a table: a row per category, a column per series. A donut has one more column, the share. This is
 * what a screen reader reads, what "Show as a table" shows and what is printed under the drawing.
 */
export function chartTable(spec: ChartSpec, words: { share?: string } = {}): ChartTable {
  const series = spec.kind === 'donut' ? spec.series.slice(0, 1) : spec.series;
  const unit = spec.unit ? ` (${spec.unit})` : '';
  const total = spec.kind === 'donut' ? (series[0]?.values ?? []).reduce((s, v) => s + Math.max(0, v || 0), 0) : 0;
  return {
    caption: spec.title,
    // the unit follows a single series' name — unless the name already is the unit ("People", in people)
    head: [spec.categoryLabel, ...series.map((s) => `${s.name}${series.length === 1 && s.name.trim().toLowerCase() !== (spec.unit ?? '').trim().toLowerCase() ? unit : ''}`), ...(spec.kind === 'donut' ? [words.share ?? 'Share'] : [])],
    rows: spec.categories.map((c, i) => [c, ...series.map((s) => chartNumber(s.values[i] ?? 0)), ...(spec.kind === 'donut' ? [`${sharePercent(Math.max(0, series[0]?.values[i] ?? 0), total)}%`] : [])]),
  };
}

/** Nothing to draw: no category, or every value is nothing. */
export const chartIsEmpty = (spec: ChartSpec): boolean => !spec.categories.length || spec.series.every((s) => s.values.every((v) => !v));

/* ------------------------------------------------------------------------------------------------
 * The scale
 * ---------------------------------------------------------------------------------------------- */

/** The axis: from 0 up to a round number at or past the largest value, in at most `steps` equal steps. */
export function chartScale(max: number, steps = 4): { max: number; ticks: number[] } {
  const top = Math.max(1, max);
  const rough = top / steps;
  const pow = Math.pow(10, Math.floor(Math.log10(rough)));
  const step = [1, 2, 5, 10].map((m) => m * pow).find((s) => s >= rough) ?? 10 * pow;
  // counts of people are whole: never a tick at 0.5
  const s = step < 1 ? 1 : step;
  const n = Math.ceil(top / s);
  return { max: n * s, ticks: Array.from({ length: n + 1 }, (_, i) => i * s) };
}

export const chartMax = (spec: ChartSpec): number => Math.max(0, ...spec.series.flatMap((s) => s.values.map((v) => v || 0)));

/* ------------------------------------------------------------------------------------------------
 * The donut
 * ---------------------------------------------------------------------------------------------- */

export interface DonutSlice {
  index: number;
  value: number;
  /** In turns of the circle: 0 is twelve o'clock, clockwise. */
  from: number;
  to: number;
  percent: number;
}

export function donutSlices(values: readonly number[]): DonutSlice[] {
  const clean = values.map((v) => Math.max(0, v || 0));
  const total = clean.reduce((s, v) => s + v, 0);
  let at = 0;
  return clean.map((value, index) => {
    const from = at;
    at += total > 0 ? value / total : 0;
    return { index, value, from, to: at, percent: sharePercent(value, total) };
  });
}

/**
 * A ring slice as an SVG path (y down), centred on cx, cy — the screen's `<path d>` and the PDF's `drawSvgPath`
 * take the same string. A whole circle is drawn as two halves (an arc cannot start and end on one point).
 */
export function ringPath(cx: number, cy: number, outer: number, inner: number, from: number, to: number): string {
  const span = Math.min(1, Math.max(0, to - from));
  if (span <= 0) return '';
  if (span >= 0.9999) return `${ringPath(cx, cy, outer, inner, 0, 0.5)} ${ringPath(cx, cy, outer, inner, 0.5, 0.9999)}`;
  const pt = (r: number, turn: number) => {
    const a = turn * 2 * Math.PI - Math.PI / 2;
    return `${(cx + r * Math.cos(a)).toFixed(2)} ${(cy + r * Math.sin(a)).toFixed(2)}`;
  };
  const large = span > 0.5 ? 1 : 0;
  return `M ${pt(outer, from)} A ${outer} ${outer} 0 ${large} 1 ${pt(outer, to)} L ${pt(inner, to)} A ${inner} ${inner} 0 ${large} 0 ${pt(inner, from)} Z`;
}

/* ------------------------------------------------------------------------------------------------
 * Colour is never the only cue
 * ---------------------------------------------------------------------------------------------- */

/**
 * Each series (each slice of a donut) has a pattern of its own beside its colour, in this order; with more than
 * six the order starts again and the labels tell them apart. A chart with one series is drawn solid and reads by
 * its labels alone.
 */
export const CHART_PATTERNS = ['solid', 'stripes', 'dots', 'cross', 'rings', 'grid'] as const;
export type ChartPattern = (typeof CHART_PATTERNS)[number];
export const patternOf = (index: number): ChartPattern => CHART_PATTERNS[index % CHART_PATTERNS.length]!;

/**
 * The series colours — the ABF purple first, then tones that keep 3:1 against a white page and against the dark
 * surface, in an order that stays apart for the common colour-blindness types. `src/styles/chart175.css` carries
 * the same six as `--chart-1 … --chart-6` (light and dark); the PDF uses `light`.
 */
export const CHART_COLOURS = {
  light: ['#481088', '#2F6FA6', '#B45309', '#135A34', '#8A1B2B', '#5F5878'],
  dark: ['#C2A6FA', '#A0D8F8', '#FBBF24', '#4CC57E', '#F06A78', '#B6ADCC'],
} as const;
